import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { IMerchantOverview, Terminal } from "../_misc/types";
import { Loader2, Wallet, Filter, ChevronDown, Search, X } from "lucide-react";
import TransactionTable from "../_misc/TransactionTable";
import { useGetMerchantDashboardOverview, useGetAllMerchantTerminal } from "../_misc/mutations";
import { useMerchantDetail } from "@/hooks/useMerchantDetail";
import { useState, useRef, useEffect } from "react";

const MerchantDashboard = () => {
  // Filter state
  const [filters, setFilters] = useState<{ [key: string]: string }>({
    responseCode: "",
    cardtype: "",
    paymentType: "",
    rrn: "",
    stan: "",
    search: "",
    terminalId: "",
    type: "",
    transactionId: "",
    status: "",
    fromDate: "",
    toDate: "",
    limit: "",
  });

  // Terminal dropdown state
  const [isTerminalDropdownOpen, setIsTerminalDropdownOpen] = useState(false);
  const [terminalSearchTerm, setTerminalSearchTerm] = useState("");
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>(null);
  const terminalDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch terminals for dropdown
  const { data: terminalsData } = useGetAllMerchantTerminal({ size: 1000, page: 1 });

  const { merchantLogin } = useMerchantDetail();
  const { data, isLoading } = useGetMerchantDashboardOverview({
    terminalId: filters?.terminalId,
  });

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle terminal selection
  const handleTerminalSelect = (terminal: Terminal) => {
    setSelectedTerminal(terminal);
    setFilters((prev) => ({
      ...prev,
      terminalId: terminal.terminalId
    }));
    setIsTerminalDropdownOpen(false);
    setTerminalSearchTerm("");
  };

  // Filter terminals based on search term
  const filteredTerminals = (terminalsData as unknown as Terminal[])?.filter((terminal: Terminal) =>
    terminal.terminalId?.toLowerCase().includes(terminalSearchTerm.toLowerCase()) ||
    terminal.terminalName?.toLowerCase().includes(terminalSearchTerm.toLowerCase())
  ) || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (terminalDropdownRef.current && !terminalDropdownRef.current.contains(event.target as Node)) {
        setIsTerminalDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <h1 className="text-2xl">Welcome, {merchantLogin?.name ?? ""}</h1>

      {/* Overview Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-700">Overview Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative" ref={terminalDropdownRef}>
            <label className="block text-sm font-medium mb-1" htmlFor="terminalId">
              Terminal ID
            </label>
            <div className="relative">
              <input
                id="terminalId"
                name="terminalId"
                type="text"
                value={selectedTerminal ? selectedTerminal.terminalId : filters.terminalId || ""}
                onChange={(e) => {
                  setTerminalSearchTerm(e.target.value);
                  if (!selectedTerminal) {
                    handleFilterChange(e);
                  }
                }}
                onFocus={() => setIsTerminalDropdownOpen(true)}
                className="border rounded px-3 py-2 w-full text-sm pr-10"
                placeholder="Search terminals..."
              />
              {/* Cancel (clear) icon */}
              {(selectedTerminal || terminalSearchTerm || filters.terminalId) && (
                <button
                  type="button"
                  className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setSelectedTerminal(null);
                    setTerminalSearchTerm("");
                    setFilters((prev) => ({ ...prev, terminalId: "" }));
                  }}
                  tabIndex={-1}
                  aria-label="Clear terminal search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsTerminalDropdownOpen(!isTerminalDropdownOpen)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label="Toggle terminal dropdown"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>

            {/* Dropdown */}
            {isTerminalDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={terminalSearchTerm}
                      onChange={(e) => setTerminalSearchTerm(e.target.value)}
                      placeholder="Search terminals..."
                      className="w-full pl-8 pr-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="py-1">
                  {filteredTerminals.length > 0 ? (
                    filteredTerminals.map((terminal) => (
                      <button
                        key={terminal._id}
                        type="button"
                        onClick={() => handleTerminalSelect(terminal)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div className="font-medium">{terminal.terminalId}</div>
                        <div className="text-xs text-gray-500">{terminal.terminalName || 'No name'}</div>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No terminals found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid place-content-center p-4">
          <Loader2 className="animate animate-spin" />
        </div>
      ) : (
        <DashboardCards data={data as IMerchantOverview} />
      )}
      <TransactionTable filters={filters} onFilterChange={handleFilterChange} />
    </div>
  );
};

export default MerchantDashboard;

const DashboardCards = ({ data }: { data: IMerchantOverview }) => {
  const {
    totalTransactionCountOfToday,
    totalTransactonsCount,
    totalTransactionVolumeOfToday,
    totalTerminalsCount,
    totalApprovedDailyTransactionsCount,
    totalDeclinedDailyTransactionsCount,
    totalDailyApprovedTransactionVolume,
    totalFailedAndApprovedTransactionVolume,
  } = data;
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 my-4">
      {/* Total Terminal */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Terminal</CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalTerminalsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">Active terminals</p>
        </CardContent>
      </Card>

      {/* Total Transaction */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Transaction</CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalTransactonsCount?.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">All-time transactions</p>
        </CardContent>
      </Card>

      {/* Total Transaction */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Transaction Today
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {totalTransactionCountOfToday?.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">Transactions today</p>
        </CardContent>
      </Card>

      {/* Total Transaction */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Transaction Volume
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            &#8358;{totalTransactionVolumeOfToday?.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">of Transactions today</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Approved Transaction Count
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {totalApprovedDailyTransactionsCount?.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            Approved transaction Today
          </p>
        </CardContent>
      </Card>

      {/* Total Transaction */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Approved Transaction Volume
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            &#8358;{totalDailyApprovedTransactionVolume?.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            of Approved transaction Today
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Declined Transaction Count
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {totalDeclinedDailyTransactionsCount?.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            Declined transaction Today
          </p>
        </CardContent>
      </Card>

      {/* Total Transaction */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Transaction Volume
          </CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            &#8358;{totalFailedAndApprovedTransactionVolume?.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            of Approved and Failed transaction.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
